import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  STATUS_DONE,
  STATUS_ACTIVE,
  STATUS_PENDING,
  type Dispatch,
} from "@/lib/types";

/** Local YYYY-MM-DD - what Supabase's appointment_date column uses. */
function localDateISO(d: Date = new Date()): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today's dispatches, queried from v_route_data. */
export function useTodayDispatches() {
  return useQuery({
    queryKey: ["dispatches", "today"],
    queryFn: async () => {
      const today = localDateISO();
      const { data, error } = await supabase
        .from("v_route_data")
        .select("*")
        .eq("appointment_date", today)
        .order("tpromtime", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as Dispatch[];
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/** Recent reminder send history (last 48h) - bounces become Attention items. */
export interface ReminderSentRow {
  id?: string;
  recipient: string | null;
  status: string | null;
  sent_at: string | null;
  dispatch?: string | null;
  customer_name?: string | null;
  status_detail?: string | null;
}

export function useRecentReminders() {
  return useQuery({
    queryKey: ["reminders", "recent"],
    queryFn: async () => {
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      const { data, error } = await supabase
        .from("reminder_sent")
        .select("*")
        .gte("sent_at", twoDaysAgo.toISOString())
        .order("sent_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as ReminderSentRow[];
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/** Tomorrow's appointments - source for "missing email" and "mismatch" attention items. */
export function useTomorrowReminderCandidates() {
  return useQuery({
    queryKey: ["reminder_candidates", "tomorrow"],
    queryFn: async () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tDate = localDateISO(tomorrow);
      const { data, error } = await supabase
        .from("v_reminder_candidates")
        .select("*")
        .eq("appointment_date", tDate);
      if (error) throw error;
      return (data ?? []) as Record<string, unknown>[];
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });
}

/** Next 7 days of dispatches - source for the week-strip capacity view. */
export function useWeekDispatches() {
  return useQuery({
    queryKey: ["dispatches", "week"],
    queryFn: async () => {
      const today = new Date();
      const weekEnd = new Date();
      weekEnd.setDate(weekEnd.getDate() + 7);
      const { data, error } = await supabase
        .from("v_route_data")
        .select(
          "dispatch,tech_empno,appointment_date,status,custno,window_code",
        )
        .gte("appointment_date", localDateISO(today))
        .lte("appointment_date", localDateISO(weekEnd));
      if (error) throw error;
      return (data ?? []) as Partial<Dispatch>[];
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });
}

// ----- Derived stats -----

/**
 * Per-tech board card shape for the Today view (consumed by TechBoardCard).
 * Currently unused — TechBoardCard isn't wired into a view yet; defined so the
 * orphan component compiles. Wire it up or remove TechBoardCard (flagged to Seth
 * 2026-06-01).
 */
export interface TechBoard {
  name: string;
  doneCount: number;
  stops: Dispatch[];
  activeStop: Dispatch | null;
  nextStop: Dispatch | null;
}

export interface TodayStats {
  total: number;
  techs: number;
  done: number;
  active: number;
  upcoming: number;
}

export function computeStats(rows: Dispatch[]): TodayStats {
  let done = 0;
  let active = 0;
  let upcoming = 0;
  const techSet = new Set<string>();
  for (const r of rows) {
    techSet.add(r.tech_empno);
    if (STATUS_DONE.has(r.status ?? "")) done++;
    else if (STATUS_ACTIVE.has(r.status ?? "")) active++;
    else if (STATUS_PENDING.has(r.status ?? "") || !r.status) upcoming++;
  }
  return {
    total: rows.length,
    techs: techSet.size,
    done,
    active,
    upcoming,
  };
}

// ----- Quality issues for tomorrow -----

const WINDOW_CODES = new Set(["8AM", "8-12", "10-2", "12-4", "3-7"]);
const ALLOWED_EMAIL_CODES = new Set([1, 255]);
const PSEUDO_CUSTOMERS = new Set(["0079253", "0079690", "0036012"]);

function getWindowForRow(r: Record<string, unknown>): {
  priWin: string | null;
  notesWin: string | null;
} {
  let priWin: string | null = null;
  for (const f of ["priority_code", "window_code", "priority"]) {
    const v = r[f];
    if (typeof v === "string" && WINDOW_CODES.has(v)) {
      priWin = v;
      break;
    }
  }

  let notesWin: string | null = null;
  const notes = r["notes"];
  if (typeof notes === "string") {
    const trimmed = notes.trimStart();
    for (const w of WINDOW_CODES) {
      const re = new RegExp(`^${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\b|[\\s,*]|$)`);
      if (re.test(trimmed)) {
        notesWin = w;
        break;
      }
    }
  }

  return { priWin, notesWin };
}

function hasEligibleEmail(r: Record<string, unknown>): boolean {
  const pairs = [
    { email: r["location_email"], code: r["location_emailtasks1"] },
    { email: r["location_email2"], code: r["location_emailtasks2"] },
  ];
  for (const p of pairs) {
    if (
      typeof p.email === "string" &&
      /^\s*\S+@\S+\.\S+\s*$/.test(p.email) &&
      typeof p.code === "number" &&
      ALLOWED_EMAIL_CODES.has(p.code)
    ) {
      return true;
    }
  }
  return false;
}

export interface QualityIssue {
  custno: string;
  locno: string | null;
  customer: string;
  address: string;
  dispatch: string;
  priWin?: string | null;
  notesWin?: string | null;
  /** The best number to call when the fix is a phone call (no-email rows). */
  phone?: string | null;
  /** No-email rows: what's left — "phone" (call/text them) or "none" (truly
   *  unreachable, no email AND no number on file). */
  reachable?: "phone" | "none";
}

/**
 * REACH-01 (Seth 2026-09-30: "shouldn't matter if they have a textable
 * number… only alert for customers with no email or text who need to be
 * actually called"): a missing email is only a PROBLEM when nothing else can
 * reach the customer. With SMS reminders ON (Settings → Reminders), a
 * no-email customer WITH a number gets a text automatically — no alert at
 * all. With SMS off, they still surface (nothing is going out), but the row
 * says the real fix. No email AND no number = unreachable, the loud one.
 */
export function computeTomorrowQualityIssues(
  rows: Record<string, unknown>[],
  opts?: { smsReminders?: boolean },
): { noEmail: QualityIssue[]; mismatch: QualityIssue[] } {
  const noEmail: QualityIssue[] = [];
  const mismatch: QualityIssue[] = [];
  const seenNoEmail = new Set<string>();
  const seenMismatch = new Set<string>();

  for (const r of rows) {
    const custno = String(r["custno"] ?? "");
    if (PSEUDO_CUSTOMERS.has(custno)) continue;
    if (r["customerinactive"] || r["location_inactive"]) continue;
    if (r["tech_dept"] !== "03") continue;
    const status = String(r["status"] ?? "");
    if (["Complete", "Done", "Transfer"].includes(status)) continue;

    const { priWin, notesWin } = getWindowForRow(r);
    const window = priWin ?? notesWin;
    if (!window) continue;

    const locno = String(r["locno"] ?? "");
    const key = `${custno}|${locno}`;
    const customer =
      (r["locname"] as string) ??
      (r["customer_fullname"] as string) ??
      "Customer";
    const address = r["service_address"]
      ? `${r["service_address"]}, ${r["city"] ?? ""}`
      : "";
    const dispatch = String(r["dispatch"] ?? "");

    if (!hasEligibleEmail(r) && !seenNoEmail.has(key)) {
      const phone = ((r["location_phone1"] ?? r["customer_phone"]) as string | null) ?? null;
      // REACH-01: SMS reminders on + a number on file = the system texts
      // them — nothing for the office to do, no row at all.
      if (!(opts?.smsReminders && phone)) {
        seenNoEmail.add(key);
        noEmail.push({ custno, locno, customer, address, dispatch, phone, reachable: phone ? "phone" : "none" });
      }
    }

    if (priWin && notesWin && priWin !== notesWin && !seenMismatch.has(key)) {
      seenMismatch.add(key);
      mismatch.push({
        custno,
        locno,
        customer,
        address,
        dispatch,
        priWin,
        notesWin,
      });
    }
  }

  return { noEmail, mismatch };
}
